import { PictureAsPdf, TableView } from "@mui/icons-material";
import { Box, Button, Chip, TextField, Tooltip } from "@mui/material";
import Paper from "@mui/material/Paper";
import {
    type GridColDef,
    GridPagination,
    type GridRowSelectionModel,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import type { Referido } from "../../firebase/firestore/referidos";
import { exportExcelReferidos, exportPDFReferidos } from "../../utils/exportTable";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import { useDateRangeStore } from "../../zustand/useDateRangeStore";
import { useReferidosStore } from "../../zustand/useReferidosStore";
import DataTable from "./DataTable";

export default function TableReferidos() {
    const { agencia } = useAgenciaStore();
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });
    const [search, setSearch] = useState("");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setSelectionModel] = useState<GridRowSelectionModel>();

    const { countTotal, allReferidos, resetReferidos, fetchNextPage, fetchCounts } =
        useReferidosStore();
    const { fechaInicio, fechaFin } = useDateRangeStore();

    // biome-ignore lint/correctness/useExhaustiveDependencies: recargar al cambiar rango
    useEffect(() => {
        resetReferidos();
        fetchCounts();
        fetchNextPage();
    }, [resetReferidos, fetchNextPage, fetchCounts, fechaInicio, fechaFin]);

    const filteredRows = allReferidos.filter((row) => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        if (row.clienteNombre?.toLowerCase().includes(s)) return true;
        if (row.beneficiarioNombre?.toLowerCase().includes(s)) return true;
        if (row.idRastreo?.toLowerCase().includes(s)) return true;
        if (row.nivel?.toLowerCase().includes(s)) return true;
        return false;
    });

    const rangeText = `${dayjs(fechaInicio).format("DD/MM/YYYY")} hasta ${dayjs(
        fechaFin
    ).format("DD/MM/YYYY")}`;

    const columns: GridColDef[] = [
        {
            field: "createdAt",
            headerName: "Fecha",
            width: 130,
            sortable: true,
            valueGetter: (_value, row) =>
                row.createdAt ? row.createdAt.toDate().getTime() : 0,
            renderCell: (params) =>
                params.row.createdAt
                    ? dayjs(params.row.createdAt.toDate()).format("DD/MM/YYYY")
                    : "-",
        },
        { field: "idRastreo", headerName: "Paquete", width: 150, sortable: true },
        {
            field: "clienteNombre",
            headerName: "Cliente (generó)",
            width: 180,
            sortable: true,
            renderCell: (params) => params.row.clienteNombre || "-",
        },
        {
            field: "beneficiarioNombre",
            headerName: "Beneficiario",
            width: 180,
            sortable: true,
            renderCell: (params) => params.row.beneficiarioNombre || "-",
        },
        {
            field: "nivel",
            headerName: "Nivel",
            width: 120,
            sortable: true,
            renderCell: (params) => {
                const nivel = params.row.nivel;
                const label =
                    nivel === "directo"
                        ? "Directo"
                        : nivel === "indirecto"
                        ? "Indirecto"
                        : "Propio";
                const color =
                    nivel === "directo"
                        ? "primary"
                        : nivel === "propio"
                        ? "success"
                        : "default";
                return (
                    <Chip
                        size="small"
                        label={label}
                        color={color}
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: "base",
            headerName: "Base",
            width: 110,
            sortable: true,
            renderCell: (params) =>
                params.row.base != null ? `$${params.row.base}` : "-",
        },
        {
            field: "porcentaje",
            headerName: "%",
            width: 80,
            sortable: true,
            renderCell: (params) => `${params.row.porcentaje}%`,
        },
        {
            field: "puntos",
            headerName: "Puntos",
            width: 100,
            sortable: true,
        },
    ];

    return (
        <Paper sx={{ padding: 2, width: "auto", marginTop: 2 }}>
            <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
                <TextField
                    label="Buscar referidos"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ width: "90%" }}
                />
                <Button
                    size="small"
                    disabled={!filteredRows.length}
                    onClick={() => {
                        const fecha = dayjs().format("DD/MM/YYYY");
                        exportPDFReferidos(
                            `referidos-${fecha}.pdf`,
                            filteredRows,
                            agencia?.nombre ?? "",
                            rangeText
                        );
                    }}
                >
                    <Tooltip title="Exportar como archivo PDF">
                        <PictureAsPdf sx={{ width: "1.2em", height: "1.2em" }} />
                    </Tooltip>
                </Button>
                <Button
                    size="small"
                    disabled={!filteredRows.length}
                    onClick={() => {
                        const fecha = dayjs().format("DD/MM/YYYY");
                        exportExcelReferidos(
                            `referidos-${fecha}.xlsx`,
                            filteredRows,
                            agencia?.nombre ?? "",
                            rangeText
                        );
                    }}
                >
                    <Tooltip title="Exportar como archivo .xlsx">
                        <TableView sx={{ width: "1.2em", height: "1.2em" }} />
                    </Tooltip>
                </Button>
            </Box>

            <DataTable<Referido>
                rows={filteredRows.slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize
                )}
                columns={columns}
                paginationModel={paginationModel}
                onSelectionModelChange={setSelectionModel}
                checkboxSelection={false}
                slots={{ footer: () => <GridPagination /> }}
                onPaginationModelChange={async (newModel) => {
                    setPaginationModel(newModel);
                    const totalLoaded = allReferidos.length;
                    const totalNeeded = (newModel.page + 1) * newModel.pageSize;
                    if (totalNeeded > totalLoaded) {
                        await fetchNextPage();
                    }
                }}
                rowCount={search ? filteredRows.length : countTotal}
                sortingMode="client"
            />
        </Paper>
    );
}
